import React, { useState } from 'react';
import { View, ScrollView, StyleSheet, TouchableOpacity, Platform, Modal, TextInput, Alert } from 'react-native';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Text, Title } from '@constants/Themed';
import DefaultScreen from '@components/Global/DefaultScreenView';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { MaterialCommunityIcons, AntDesign } from '@expo/vector-icons';
import apis from '@api/api';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';

const PerformanceReviews = () => {
  const theme = themeSelector();
  const currentCompany = useSelector((state: DefaultRootState) => state.app.selectedProfile);
  const queryClient = useQueryClient();
  const [filterStatus, setFilterStatus] = useState<string | undefined>(undefined);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newReview, setNewReview] = useState({
    employeeId: '',
    reviewPeriod: new Date().toISOString().split('T')[0],
    rating: 3,
    goals: [] as string[],
    achievements: [] as string[],
    areasForImprovement: [] as string[]
  });
  const [tempGoal, setTempGoal] = useState('');
  const [tempAchievement, setTempAchievement] = useState('');
  const [tempImprovement, setTempImprovement] = useState('');

  const { data: reviewsData, isLoading } = useQuery({
    queryKey: ['hrPerformanceReviews', currentCompany?._id, filterStatus],
    queryFn: async () => {
      const response = await apis.getPerformanceReviews({ status: filterStatus });
      return response?.data || [];
    },
    enabled: !!currentCompany?._id
  });

  const createReviewMutation = useMutation({
    mutationFn: (data: any) => apis.createPerformanceReview(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hrPerformanceReviews'] });
      setShowCreateModal(false);
      setNewReview({
        employeeId: '',
        reviewPeriod: new Date().toISOString().split('T')[0],
        rating: 3,
        goals: [],
        achievements: [],
        areasForImprovement: []
      });
      Alert.alert('Success', 'Performance review created successfully');
    },
    onError: (error: any) => {
      Alert.alert('Error', error.message || 'Failed to create performance review');
    }
  });

  const reviews = reviewsData || [];

  const getRatingColor = (rating: number) => {
    if (rating >= 4) return '#10b981';
    if (rating >= 3) return '#f59e0b';
    return '#ef4444';
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }).map((_, index) => (
      <AntDesign
        key={index}
        name={index < rating ? 'star' : 'staro'}
        size={16}
        color={index < rating ? getRatingColor(rating) : '#d1d5db'}
      />
    ));
  };

  return (
    <DefaultScreen>
      <View style={styles.container}>
        <View style={styles.header}>
          <View style={styles.headerTop}>
            <Title style={{ fontSize: 28, fontWeight: '700', color: theme ? '#111827' : '#f9fafb' }}>
              Performance Reviews
            </Title>
            <TouchableOpacity
              style={[styles.createButton, { backgroundColor: '#3b82f6' }]}
              onPress={() => setShowCreateModal(true)}
            >
              <AntDesign name="plus" size={20} color="#ffffff" />
              <Text style={styles.createButtonText}>New Review</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Filter */}
        <View style={styles.filterContainer}>
          <TouchableOpacity
            style={[
              styles.filterButton,
              filterStatus === undefined && { backgroundColor: '#3b82f6' }
            ]}
            onPress={() => setFilterStatus(undefined)}
          >
            <Text style={styles.filterText}>All</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[
              styles.filterButton,
              filterStatus === 'draft' && { backgroundColor: '#6b7280' }
            ]}
            onPress={() => setFilterStatus('draft')}
          >
            <Text style={styles.filterText}>Draft</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[
              styles.filterButton,
              filterStatus === 'completed' && { backgroundColor: '#10b981' }
            ]}
            onPress={() => setFilterStatus('completed')}
          >
            <Text style={styles.filterText}>Completed</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[
              styles.filterButton,
              filterStatus === 'acknowledged' && { backgroundColor: '#3b82f6' }
            ]}
            onPress={() => setFilterStatus('acknowledged')}
          >
            <Text style={styles.filterText}>Acknowledged</Text>
          </TouchableOpacity>
        </View>

        {isLoading ? (
          <View style={styles.loadingContainer}>
            <Text>Loading...</Text>
          </View>
        ) : (
          <ScrollView showsVerticalScrollIndicator={false}>
            {reviews.map((review: any) => (
              <View
                key={review._id}
                style={[
                  styles.reviewCard,
                  {
                    backgroundColor: theme ? '#ffffff' : '#1f2937',
                    borderColor: theme ? '#e5e7eb' : '#374151'
                  }
                ]}
              >
                <View style={styles.reviewHeader}>
                  <View style={styles.reviewInfo}>
                    <Text style={[styles.reviewPeriod, { color: theme ? '#111827' : '#f9fafb' }]}>
                      {new Date(review.reviewPeriod).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </Text>
                    <View style={styles.ratingContainer}>
                      {renderStars(review.rating)}
                      <Text style={[styles.ratingText, { color: theme ? '#6b7280' : '#9ca3af' }]}>
                        ({review.rating}/5)
                      </Text>
                    </View>
                  </View>
                  <View style={[
                    styles.statusBadge,
                    { backgroundColor: review.status === 'completed' ? '#10b98120' : '#6b728020' }
                  ]}>
                    <Text style={[
                      styles.statusText,
                      { color: review.status === 'completed' ? '#10b981' : '#6b7280' }
                    ]}>
                      {review.status}
                    </Text>
                  </View>
                </View>

                {review.achievements && review.achievements.length > 0 && (
                  <View style={styles.section}>
                    <Text style={[styles.sectionTitle, { color: theme ? '#111827' : '#f9fafb' }]}>
                      Achievements
                    </Text>
                    {review.achievements.map((achievement: string, index: number) => (
                      <View key={index} style={styles.listItem}>
                        <MaterialCommunityIcons name="check-circle" size={16} color="#10b981" />
                        <Text style={[styles.listItemText, { color: theme ? '#111827' : '#f9fafb' }]}>
                          {achievement}
                        </Text>
                      </View>
                    ))}
                  </View>
                )}

                {review.areasForImprovement && review.areasForImprovement.length > 0 && (
                  <View style={styles.section}>
                    <Text style={[styles.sectionTitle, { color: theme ? '#111827' : '#f9fafb' }]}>
                      Areas for Improvement
                    </Text>
                    {review.areasForImprovement.map((area: string, index: number) => (
                      <View key={index} style={styles.listItem}>
                        <MaterialCommunityIcons name="alert-circle" size={16} color="#f59e0b" />
                        <Text style={[styles.listItemText, { color: theme ? '#111827' : '#f9fafb' }]}>
                          {area}
                        </Text>
                      </View>
                    ))}
                  </View>
                )}
              </View>
            ))}
            {reviews.length === 0 && (
              <View style={styles.emptyContainer}>
                <MaterialCommunityIcons name="star-outline" size={48} color={theme ? '#9ca3af' : '#6b7280'} />
                <Text style={[styles.emptyText, { color: theme ? '#6b7280' : '#9ca3af' }]}>
                  No performance reviews found
                </Text>
              </View>
            )}
          </ScrollView>
        )}

        {/* Create Review Modal */}
        <Modal
          visible={showCreateModal}
          transparent={true}
          animationType="slide"
          onRequestClose={() => setShowCreateModal(false)}
        >
          <View style={[styles.modalOverlay, { backgroundColor: 'rgba(0,0,0,0.5)' }]}>
            <View style={[styles.modalContent, { backgroundColor: theme ? '#ffffff' : '#1f2937' }]}>
              <View style={styles.modalHeader}>
                <Title style={{ fontSize: 20, fontWeight: '700', color: theme ? '#111827' : '#f9fafb' }}>
                  Create Performance Review
                </Title>
                <TouchableOpacity onPress={() => setShowCreateModal(false)}>
                  <AntDesign name="close" size={24} color={theme ? '#111827' : '#f9fafb'} />
                </TouchableOpacity>
              </View>

              <ScrollView showsVerticalScrollIndicator={false}>
                <View style={styles.formGroup}>
                  <Text style={[styles.label, { color: theme ? '#111827' : '#f9fafb' }]}>Review Period</Text>
                  <TextInput
                    style={[styles.input, { backgroundColor: theme ? '#f3f4f6' : '#374151', color: theme ? '#111827' : '#f9fafb' }]}
                    value={newReview.reviewPeriod}
                    onChangeText={(text) => setNewReview({ ...newReview, reviewPeriod: text })}
                    placeholder="YYYY-MM-DD"
                  />
                </View>

                <View style={styles.formGroup}>
                  <Text style={[styles.label, { color: theme ? '#111827' : '#f9fafb' }]}>Rating</Text>
                  <View style={styles.ratingSelector}>
                    {[1, 2, 3, 4, 5].map((rating) => (
                      <TouchableOpacity
                        key={rating}
                        onPress={() => setNewReview({ ...newReview, rating })}
                      >
                        <AntDesign
                          name={rating <= newReview.rating ? 'star' : 'staro'}
                          size={32}
                          color={rating <= newReview.rating ? '#f59e0b' : '#d1d5db'}
                        />
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>

                <View style={styles.formGroup}>
                  <Text style={[styles.label, { color: theme ? '#111827' : '#f9fafb' }]}>Achievements</Text>
                  <View style={styles.listInputContainer}>
                    <TextInput
                      style={[styles.listInput, { backgroundColor: theme ? '#f3f4f6' : '#374151', color: theme ? '#111827' : '#f9fafb' }]}
                      value={tempAchievement}
                      onChangeText={setTempAchievement}
                      placeholder="Add achievement..."
                      onSubmitEditing={() => {
                        if (tempAchievement.trim()) {
                          setNewReview({
                            ...newReview,
                            achievements: [...newReview.achievements, tempAchievement.trim()]
                          });
                          setTempAchievement('');
                        }
                      }}
                    />
                    <TouchableOpacity
                      style={[styles.addButton, { backgroundColor: '#10b981' }]}
                      onPress={() => {
                        if (tempAchievement.trim()) {
                          setNewReview({
                            ...newReview,
                            achievements: [...newReview.achievements, tempAchievement.trim()]
                          });
                          setTempAchievement('');
                        }
                      }}
                    >
                      <AntDesign name="plus" size={16} color="#ffffff" />
                    </TouchableOpacity>
                  </View>
                  {newReview.achievements.map((item, index) => (
                    <View key={index} style={styles.listItem}>
                      <Text style={[styles.listItemText, { color: theme ? '#111827' : '#f9fafb' }]}>{item}</Text>
                      <TouchableOpacity
                        onPress={() => {
                          setNewReview({
                            ...newReview,
                            achievements: newReview.achievements.filter((_, i) => i !== index)
                          });
                        }}
                      >
                        <AntDesign name="close" size={16} color="#ef4444" />
                      </TouchableOpacity>
                    </View>
                  ))}
                </View>

                <View style={styles.formGroup}>
                  <Text style={[styles.label, { color: theme ? '#111827' : '#f9fafb' }]}>Areas for Improvement</Text>
                  <View style={styles.listInputContainer}>
                    <TextInput
                      style={[styles.listInput, { backgroundColor: theme ? '#f3f4f6' : '#374151', color: theme ? '#111827' : '#f9fafb' }]}
                      value={tempImprovement}
                      onChangeText={setTempImprovement}
                      placeholder="Add area for improvement..."
                      onSubmitEditing={() => {
                        if (tempImprovement.trim()) {
                          setNewReview({
                            ...newReview,
                            areasForImprovement: [...newReview.areasForImprovement, tempImprovement.trim()]
                          });
                          setTempImprovement('');
                        }
                      }}
                    />
                    <TouchableOpacity
                      style={[styles.addButton, { backgroundColor: '#f59e0b' }]}
                      onPress={() => {
                        if (tempImprovement.trim()) {
                          setNewReview({
                            ...newReview,
                            areasForImprovement: [...newReview.areasForImprovement, tempImprovement.trim()]
                          });
                          setTempImprovement('');
                        }
                      }}
                    >
                      <AntDesign name="plus" size={16} color="#ffffff" />
                    </TouchableOpacity>
                  </View>
                  {newReview.areasForImprovement.map((item, index) => (
                    <View key={index} style={styles.listItem}>
                      <Text style={[styles.listItemText, { color: theme ? '#111827' : '#f9fafb' }]}>{item}</Text>
                      <TouchableOpacity
                        onPress={() => {
                          setNewReview({
                            ...newReview,
                            areasForImprovement: newReview.areasForImprovement.filter((_, i) => i !== index)
                          });
                        }}
                      >
                        <AntDesign name="close" size={16} color="#ef4444" />
                      </TouchableOpacity>
                    </View>
                  ))}
                </View>

                <TouchableOpacity
                  style={[styles.submitButton, { backgroundColor: '#3b82f6' }]}
                  onPress={() => {
                    if (!newReview.employeeId) {
                      Alert.alert('Error', 'Please select an employee');
                      return;
                    }
                    createReviewMutation.mutate(newReview);
                  }}
                  disabled={createReviewMutation.isPending}
                >
                  <Text style={styles.submitButtonText}>
                    {createReviewMutation.isPending ? 'Creating...' : 'Create Review'}
                  </Text>
                </TouchableOpacity>
              </ScrollView>
            </View>
          </View>
        </Modal>
      </View>
    </DefaultScreen>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20
  },
  header: {
    marginBottom: 24
  },
  filterContainer: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 20
  },
  filterButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: '#e5e7eb'
  },
  filterText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#111827'
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40
  },
  reviewCard: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 12
  },
  reviewHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16
  },
  reviewInfo: {
    flex: 1
  },
  reviewPeriod: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 8
  },
  ratingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4
  },
  ratingText: {
    fontSize: 14,
    marginLeft: 4
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 6
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'capitalize'
  },
  section: {
    marginBottom: 16
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 8
  },
  listItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
    gap: 8
  },
  listItemText: {
    flex: 1,
    fontSize: 14
  },
  emptyContainer: {
    padding: 40,
    alignItems: 'center'
  },
  emptyText: {
    fontSize: 16,
    marginTop: 12
  },
  headerTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  createButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    gap: 8
  },
  createButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600'
  },
  modalOverlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.5)'
  },
  modalContent: {
    width: '90%',
    maxWidth: 600,
    maxHeight: '80%',
    padding: 24,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20
  },
  formGroup: {
    marginBottom: 20
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8
  },
  input: {
    padding: 12,
    borderRadius: 8,
    fontSize: 16
  },
  ratingSelector: {
    flexDirection: 'row',
    gap: 8
  },
  listInputContainer: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 8
  },
  listInput: {
    flex: 1,
    padding: 12,
    borderRadius: 8,
    fontSize: 16
  },
  addButton: {
    padding: 12,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center'
  },
  listItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 12,
    borderRadius: 8,
    backgroundColor: '#f3f4f6',
    marginBottom: 8
  },
  listItemText: {
    flex: 1,
    fontSize: 14
  },
  submitButton: {
    padding: 16,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 20
  },
  submitButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600'
  }
});

export default PerformanceReviews;

